/**
 * Single source of truth for reading Hasura claims off an Nhost access token.
 *
 * Prefer `session.decodedToken` (the SDK already decodes the JWT and converts Postgres arrays to
 * JS arrays) and pass it to `claimsFromDecodedToken`. Use `decodeJwtPayload` only when you have a
 * raw token string and no SDK session — e.g. the edge proxy reading the cookie, or the minted
 * impersonation token which is never stored in the Nhost session.
 *
 * The signature is NOT verified here: the client/edge only reads claims for routing/display; Hasura
 * verifies the token and RLS is the real authorization boundary.
 */

/** The Hasura JWT custom-claims namespace. */
export const NS = 'https://hasura.io/jwt/claims'

/** Hasura claims we read off a TaskyInn access token. */
export interface HasuraClaims {
  userId: string
  role: string | null
  allowedRoles: string[]
  tenantId: string | null
  activePropertyId: string | null
  allTenantIds: string[]
  propertyIds: string[]
}

/**
 * Decode a JWT payload (no signature check). Isomorphic — `atob` exists in browsers and Node ≥16.
 * Returns null on any malformed input.
 */
export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const part = token.split('.')[1]
    if (!part) return null
    const b64 = part
      .replace(/-/g, '+')
      .replace(/_/g, '/')
      .padEnd(Math.ceil(part.length / 4) * 4, '=')
    return JSON.parse(atob(b64)) as Record<string, unknown>
  } catch {
    return null
  }
}

/**
 * Hasura serializes array session vars as a Postgres literal `{"a","b"}` in the raw JWT, but the
 * SDK's `decodedToken` has already converted them to JS arrays. Handle both.
 */
export function parsePgArray(value: unknown): string[] {
  if (Array.isArray(value)) return value.filter((v): v is string => typeof v === 'string')
  if (typeof value !== 'string') return []
  return value
    .replace(/^\{|\}$/g, '')
    .split(',')
    // Trim FIRST: `{"U", "T"}` (a space after the comma) kept the quotes of every
    // later element and lost it (review round 2, S-5).
    .map((s) => s.trim().replace(/^"|"$/g, '').trim())
    .filter(Boolean)
}

/**
 * Extract the Hasura claims from a decoded JWT payload (`session.decodedToken` or the result of
 * `decodeJwtPayload`). Returns null when there is no user id (signed out / unreadable).
 *
 * `x-hasura-default-role` IS the active role — tenant/property switch re-mints the token and the
 * client refreshes, so the default role always reflects the active selection.
 */
export function claimsFromDecodedToken(
  decoded: Record<string, unknown> | undefined | null,
): HasuraClaims | null {
  const c = decoded?.[NS] as Record<string, unknown> | undefined
  const userId = c?.['x-hasura-user-id']
  if (typeof userId !== 'string' || !userId) return null
  // E2E-1 D10: a user whose auth metadata is JSON null is minted the claim "null" - never a value
  // (the dashboard sent it as a uuid and stayed on skeletons).
  const asString = (v: unknown): string | null => (typeof v === 'string' && v && v !== 'null' && v !== 'undefined' ? v : null)
  return {
    userId,
    role: asString(c?.['x-hasura-default-role']),
    allowedRoles: parsePgArray(c?.['x-hasura-allowed-roles']),
    tenantId: asString(c?.['x-hasura-tenant-id']),
    activePropertyId: asString(c?.['x-hasura-active-property-id']),
    allTenantIds: parsePgArray(c?.['x-hasura-all-tenant-id']),
    propertyIds: parsePgArray(c?.['x-hasura-property-id']),
  }
}
